/*
 * These columns define all available columns across the application. Subsequent
 * objects should be derived from this.
 *
 * It's assumed that all columns present will be rendered in the view. Options
 * are available to turn them off in the sort menu only.
 *
 * disabled: Visible in the sort menu, but checked and disabled
 * hidden: Not visibile in the sort menu
 */

import orderBy from "lodash.orderby";
import { I18n } from "@lingui/core";
import { t } from "@lingui/macro";
import { createSortMap } from "./sorting";
import moment from "moment";

export type Direction = "asc" | "desc";

export enum Columns {
  ID = "ID",
  PRIORITY = "PRIORITY",
  SITE = "SITE",
  SUMMARY = "SUMMARY",
  CONTEXT = "CONTEXT",
  RESPONSIBLE = "RESPONSIBLE",
  DATES = "DATES",
  STATUS = "STATUS",
  LATEST = "LATEST",
  REGION = "REGION",
  RULE_NAME = "RULE_NAME",
  EQUIP_NAME = "EQUIP_NAME",
  EQUIP_DETAILS = "EQUIP_DETAILS",
  TRIGGERED_RULES = "TRIGGERED_RULES",
  AFFECTED_EQUIPS = "AFFECTED_EQUIPS",
  RULE_TYPE = "RULE_TYPE",
  SEVERITY = "SEVERITY",
  ACTIONS_COUNT = "ACTIONS_COUNT",
}

export interface ColumnOption {
  key: string;
  label: (i18n: I18n) => any;
  // TODO: `width` will be removed in favour of `minWidth`.
  width: string; // FIXME: Remove it after full migration to new data grid.
  minWidth?: string;
  sortFn: <T>(data: T[], direction: Direction) => T[];
  sortDirection: Direction;
  disabled: boolean;
  hidden: boolean;
}

export interface ColumnOptionMap {
  [key: string]: ColumnOption;
}

const prioritySortMap = createSortMap(
  ["Blocker", "Critical", "Major", "Moderate", "Minor", "Trivial"],
  true
);

const ID: ColumnOption = {
  key: Columns.ID,
  label: (i18n) => i18n._(t("columns.id")`ID`),
  width: "70px",
  sortFn: (issues: any[] /* @TODO */, direction) =>
    orderBy(
      issues,
      (issue) => {
        if (issue.key === undefined) return 1;
        return Number(issue.key.split("-")[1]);
      },
      direction
    ),
  sortDirection: "desc",
  disabled: true,
  hidden: false,
};

const PRIORITY: ColumnOption = {
  key: Columns.PRIORITY,
  label: (i18n) => i18n._(t("columns.priority")`Priority`),
  width: "120px",
  sortFn: (issues: any[] /* @TODO */, direction) =>
    orderBy(issues, (issue) => prioritySortMap[issue.priority], direction),
  sortDirection: "desc",
  disabled: false,
  hidden: false,
};

const MONITORING_PRIORITY: ColumnOption = {
  key: Columns.PRIORITY,
  label: (i18n) => i18n._(t("columns.priority")`Priority`),
  width: "120px",
  sortFn: (insights: any[] /* @TODO */, direction) =>
    orderBy(insights, (i) => i.priority, direction),
  sortDirection: "desc",
  disabled: false,
  hidden: false,
};

const SITE: ColumnOption = {
  key: Columns.SITE,
  label: (i18n) => i18n._(t("columns.site")`Site`),
  width: "180px",
  sortFn: (issues: any[] /* @TODO */, direction) =>
    orderBy(issues, (issue) => issue.components.toLowerCase(), direction),
  sortDirection: "asc",
  disabled: false,
  hidden: false,
};

const SUMMARY: ColumnOption = {
  key: Columns.SUMMARY,
  label: (i18n) => i18n._(t("columns.summary")`Summary`),
  width: "290px",
  sortFn: (issues: any[] /* @TODO */, direction) =>
    // NOTE: The issue summary may sometimes have a leading space, thus
    // calling `.trim()` here. This should (in my opinion) be resolved by
    // trimming before it's served to the frontend. -- Jack 11/01/2019
    orderBy(issues, (issue) => issue.summary.toLowerCase().trim(), direction),
  sortDirection: "asc",
  disabled: true,
  hidden: false,
};

const CONTEXT: ColumnOption = {
  key: Columns.CONTEXT,
  label: (i18n) => i18n._(t("columns.type")`Type`),
  width: "110px",
  sortFn: (issues: any[] /* @TODO */, direction) =>
    orderBy(issues, (issue) => issue.context.toLowerCase(), direction),
  sortDirection: "asc",
  disabled: false,
  hidden: false,
};

const RESPONSIBLE: ColumnOption = {
  key: Columns.RESPONSIBLE,
  label: (i18n) => i18n._(t("columns.responsible")`Responsible`),
  width: "140px",
  sortFn: (issues: any[] /* @TODO */, direction) =>
    orderBy(issues, (issue) => issue.responsible.toLowerCase(), direction),
  sortDirection: "asc",
  disabled: false,
  hidden: false,
};

const DATES: ColumnOption = {
  key: Columns.DATES,
  label: (i18n) => i18n._(t("columns.updated")`Updated`),
  width: "120px",
  sortFn: (issues: any[] /* @TODO */, direction = "desc") =>
    orderBy(issues, (issue) => moment(issue.updated).unix(), direction),
  sortDirection: "desc",
  disabled: false,
  hidden: false,
};

const STATUS: ColumnOption = {
  key: Columns.STATUS,
  label: (i18n) => i18n._(t("columns.status")`Status`),
  width: "115px",
  sortFn: (issues: any[] /* @TODO */, direction) =>
    orderBy(issues, (issue) => issue.bonfire_status.toLowerCase(), direction),
  sortDirection: "asc",
  disabled: false,
  hidden: false,
};

const LATEST: ColumnOption = {
  key: Columns.LATEST,
  label: (i18n) => i18n._(t("columns.latest")`Latest`),
  width: "90px",
  minWidth: "90px",
  sortFn: (summaries: any[] /* @TODO */, direction) =>
    orderBy(summaries, (s) => s.latestUnix, direction),
  sortDirection: "desc",
  disabled: false,
  hidden: false,
};

const REGION: ColumnOption = {
  key: Columns.REGION,
  label: (i18n) => i18n._(t("columns.region")`Region`),
  width: "80px",
  sortFn: (summaries: any[] /* @TODO */, direction) =>
    orderBy(summaries, (s) => s.site.state, direction),
  sortDirection: "asc",
  disabled: false,
  hidden: false,
};

const RULE_NAME: ColumnOption = {
  key: Columns.RULE_NAME,
  label: (i18n) => i18n._(t("columns.rule_name")`Rule`),
  width: "420px",
  sortFn: (summaries: any[] /* @TODO */, direction) =>
    orderBy(summaries, (r) => r.ruleName, direction),
  sortDirection: "asc",
  disabled: false,
  hidden: false,
};

const EQUIP_NAME: ColumnOption = {
  key: Columns.EQUIP_NAME,
  label: (i18n) => i18n._(t("columns.equip_name")`Equip`),
  width: "140px",
  sortFn: (summaries: any[] /* @TODO */, direction) =>
    orderBy(summaries, (r) => r.targetName, direction),
  sortDirection: "asc",
  disabled: false,
  hidden: false,
};

const EQUIP_DETAILS: ColumnOption = {
  key: Columns.EQUIP_DETAILS,
  label: (i18n) => i18n._(t("columns.equip_details")`Details`),
  width: "350px",
  sortFn: (hits: any[] /* @TODO */, direction) =>
    orderBy(hits, (h) => h.description, direction),
  sortDirection: "asc",
  disabled: false,
  hidden: false,
};

const TRIGGERED_RULES: ColumnOption = {
  key: Columns.TRIGGERED_RULES,
  label: (i18n) => i18n._(t("columns.triggered_rules")`Hits`),
  width: "100px",
  minWidth: "120px",
  sortFn: (summaries: any[] /* @TODO */, direction) =>
    orderBy(summaries, (s) => s.activeHits, direction),
  sortDirection: "desc",
  disabled: false,
  hidden: false,
};

const AFFECTED_EQUIPS: ColumnOption = {
  key: Columns.AFFECTED_EQUIPS,
  label: (i18n) => i18n._(t("columns.affected_equips")`Equips`),
  width: "100px",
  minWidth: "120px",
  sortFn: (summaries: any[] /* @TODO */, direction) =>
    orderBy(summaries, (s) => s.activeTargetCount, direction),
  sortDirection: "desc",
  disabled: false,
  hidden: false,
};

const RULE_TYPE: ColumnOption = {
  key: Columns.RULE_TYPE,
  label: (i18n) => i18n._(t("columns.rule_type")`Rule Type`),
  width: "120px",
  minWidth: "100px",
  sortFn: (summaries: any[] /* @TODO */, direction) =>
    orderBy(
      summaries,
      (r) => (r.ruleType === "" ? "~" : r.ruleType),
      direction
    ),
  sortDirection: "asc",
  disabled: false,
  hidden: false,
};

const SEVERITY: ColumnOption = {
  key: Columns.SEVERITY,
  label: (i18n) => i18n._(t("columns.severity")`Severity`),
  width: "110px",
  sortFn: (summaries: any[] /* @TODO */, direction) =>
    orderBy(
      summaries,
      [(r) => prioritySortMap[r.severity], (r) => r.latestUnix],
      direction
    ),
  sortDirection: "desc",
  disabled: false,
  hidden: false,
};

const ACTIONS_COUNT: ColumnOption = {
  key: Columns.ACTIONS_COUNT,
  label: (i18n) => i18n._(t("columns.actions_count")`Actions`),
  width: "100px",
  sortFn: (summaries: any[] /* @TODO */, direction) =>
    orderBy(summaries, [(s) => s.actionsCount, (r) => r.severity], direction),
  sortDirection: "desc",
  disabled: false,
  hidden: false,
};

export const actionColumns = {
  ID,
  PRIORITY,
  SITE,
  SUMMARY,
  CONTEXT,
  RESPONSIBLE,
  DATES,
  STATUS,
};

export const insightColumns: ColumnOptionMap = {
  LATEST,
  PRIORITY: MONITORING_PRIORITY,
  SITE: {
    ...SITE,
    width: "450px",
    sortFn: (hits: any[] /* @TODO */, direction) =>
      orderBy(hits, (h) => h.site.label, direction),
  },
  REGION,
  RULE_NAME,
  TRIGGERED_RULES,
  AFFECTED_EQUIPS,
  RULE_TYPE,
  SEVERITY,
  ACTIONS_COUNT,
};

export const ruleColumns: ColumnOptionMap = {
  LATEST,
  SEVERITY,
  RULE_NAME,
  AFFECTED_EQUIPS: {
    ...AFFECTED_EQUIPS,
    width: "220px",
  },
  RULE_TYPE,
  ACTIONS_COUNT,
};

export const equipColumns: ColumnOptionMap = {
  LATEST: {
    ...LATEST,
    sortFn: (summaries: any[] /* @TODO */, direction) =>
      orderBy(summaries, (s) => moment(s.occurredOn).unix(), direction),
  },
  SEVERITY,
  EQUIP_NAME,
  EQUIP_DETAILS,
  TRIGGERED_RULES: {
    ...TRIGGERED_RULES,
    width: "140px",
    sortFn: (hits: any[] /* @TODO */, direction) =>
      orderBy(hits, (h) => h.times.length, direction),
  },
  RULE_TYPE,
  ACTIONS_COUNT: {
    ...ACTIONS_COUNT,
    sortFn: (hits: any[] /* @TODO */, direction) =>
      orderBy(
        hits,
        [(h) => (h.issueKey === undefined ? "~" : h.issueKey)],
        direction
      ),
    sortDirection: "asc",
  },
};

export const monitoringColumns: ColumnOptionMap = {
  LATEST,
  PRIORITY: MONITORING_PRIORITY,
  SITE: {
    ...SITE,
    width: "450px",
    sortFn: (hits: any[] /* @TODO */, direction) =>
      orderBy(hits, (h) => h.site.label, direction),
  },
  REGION,
  RULE_NAME,
  TRIGGERED_RULES,
  AFFECTED_EQUIPS,
  RULE_TYPE,
  SEVERITY,
  ACTIONS_COUNT,
};

export const analysisColumns = {
  monitoring: monitoringColumns,
  insights: insightColumns,
};
